// Pure auth helpers — no React, no Supabase imports, fully unit-testable.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MIN_PASSWORD_LENGTH = 8;

export function isValidEmail(email) {
  return typeof email === 'string' && EMAIL_RE.test(email.trim());
}

// Returns an error message (string) or null when the credentials are OK.
// `requireStrongPassword` is true for sign-up, false for login (existing
// accounts may predate the length rule).
export function validateCredentials(email, password, { requireStrongPassword = false } = {}) {
  if (!email || !email.trim()) return 'Please enter your email address.';
  if (!isValidEmail(email)) return 'Please enter a valid email address.';
  if (!password) return 'Please enter your password.';
  if (requireStrongPassword && password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  return null;
}

// Maps Supabase auth errors to short, user-friendly English messages.
export function friendlyAuthError(error) {
  if (!error) return null;
  const message = (typeof error === 'string' ? error : error.message) || '';
  const normalized = message.toLowerCase();
  if (normalized.includes('invalid login credentials')) {
    return 'Wrong email or password.';
  }
  if (normalized.includes('email not confirmed')) {
    return 'Please confirm your email first — check your inbox.';
  }
  if (
    normalized.includes('user already registered') ||
    normalized.includes('already been registered')
  ) {
    return 'An account with this email already exists. Try logging in.';
  }
  if (normalized.includes('rate limit') || normalized.includes('too many requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (normalized.includes('network') || normalized.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  return message || 'Something went wrong. Please try again.';
}

// Short label for the header button, e.g. "chief@ship.com" -> "chief".
export function accountLabel(user) {
  const email = user?.email || '';
  if (!email) return 'Account';
  const name = email.split('@')[0];
  return name.length > 14 ? `${name.slice(0, 12)}…` : name;
}
